import { useQuery } from '@tanstack/react-query';
import { api } from '@weebee/api';
import { queryKeys } from '../query-keys';
import type { Period } from '@/components/shared/period-selector';

export function useCompanyDashboard(slug: string, days: number) {
  return useQuery({
    queryKey: queryKeys.corporate.dashboard(slug, days),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/corporate/companies/{slug}/dashboard', {
        params: { path: { slug }, query: { dashboard_days: days } },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCompanyLeaderboard(slug: string, period: Period, limit = 20, offset = 0) {
  return useQuery({
    queryKey: queryKeys.corporate.leaderboard(slug, `${period}-${limit}-${offset}`),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/corporate/companies/{slug}/leaderboard', {
        params: {
          path: { slug },
          query: { company_leaderboard_period: period, limit, offset },
        },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

export function useCompanyMembers(slug: string, limit = 20, offset = 0) {
  return useQuery({
    queryKey: queryKeys.corporate.members(slug, { limit, offset }),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/corporate/companies/{slug}/members', {
        params: {
          path: { slug },
          query: { limit, offset },
        },
      });
      if (error) throw error;
      return data;
    },
  });
}

export function useMemberStats(slug: string, userId: string) {
  return useQuery({
    queryKey: queryKeys.corporate.memberStats(slug, userId),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/api/v1/corporate/companies/{slug}/members/{userId}/stats',
        { params: { path: { slug, userId } } }
      );
      if (error) throw error;
      return data;
    },
  });
}

export function useCompanyDomains(slug: string) {
  return useQuery({
    queryKey: queryKeys.corporate.domains(slug),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/corporate/companies/{slug}/domains', {
        params: { path: { slug } },
      });
      if (error) throw error;
      return data;
    },
  });
}
